import React from 'react';
import { clsx } from 'clsx';
import ExpandableSection from '@amzn/awsui-components-react/polaris/expandable-section';
import { renderReact } from '../../utils/markdown.plugin.utils.js';
import sanitizeHtml from '../../utils/sanitize.utils.js';

/**
 * Code based of off markdown-it-collapsible
 * https://github.com/Bioruebe/markdown-it-collapsible
 * Version: 1.0.0
 *
 * Updates:
 * Added parameters block to pass props to Polaris ExpandableSection component
 * Removed code that pushed tokens for summary section
 * Added renderCollapsible function to render React component
 */
const renderCollapsible = (tokens, idx, options, env, self) => {
  if (!sanitizeHtml(self.render(tokens[idx].children, options, env))) return;
  return renderReact(_ref => {
    let {
      content
    } = _ref;
    const {
      headerText,
      headingLevel,
      isOpen,
      variant
    } = content.meta;
    return /*#__PURE__*/React.createElement(ExpandableSection, {
      className: clsx('bork-expandable', variant === 'container' && 'bork-expandable--container'),
      headerText: headerText,
      defaultExpanded: isOpen,
      variant: variant,
      headingTagOverride: headingLevel,
      disableContentPaddings: true
    }, /*#__PURE__*/React.createElement("div", {
      className: clsx(variant === 'container' ? 'bork-expandable--container-content' : 'bork-expandable-content'),
      dangerouslySetInnerHTML: {
        __html: self.render(tokens[idx].children, options, env)
      }
    }));
  })({
    content: tokens[idx],
    origRender: '',
    env,
    tokens
  });
};
const isWhitespace = (state, start, end) => {
  for (start; start < end; start++) {
    if (!state.md.utils.isWhiteSpace(state.src.charCodeAt(start))) return false;
  }
  return true;
};
const PLUS_MARKER = 43; // +

// The core plugin which checks for the appropriate prefix content of
// `+++` for an collapsible block and passed params `{{ params }}`
const coreRule = (state, startLine, endLine, silent) => {
  let isExpandable = true;
  let titleCount = 0;

  // does the block auto close?
  let autoClosedBlock = false;
  let start = state.bMarks[startLine] + state.tShift[startLine];
  let max = state.eMarks[startLine];
  if (state.src.charCodeAt(start) !== PLUS_MARKER) {
    isExpandable = false;
  }
  // if block doesn't start with OPEN or CLOSED character than ignore
  if (!isExpandable) return false;

  // Check out the rest of the marker string
  let pos = state.skipChars(start, PLUS_MARKER);
  const markerCount = pos - start;
  if (markerCount < 3) return false;
  const params = state.src.slice(pos, max).trim();
  const markerFence = String.fromCharCode(PLUS_MARKER).repeat(markerCount); // +++

  // Title must not be empty
  if (isWhitespace(state, pos, max)) return false;

  // The title must not end with the marker (no inline)
  if (params.endsWith(markerFence)) {
    return false;
  }

  // Since start is found, we can report success here in validation mode
  if (silent) return true;

  // Search the end of the block
  let nextLine = startLine;
  let isEmpty = true;
  for (;;) {
    nextLine++;

    // If current line begins with +++ and remainder of line after markers is not empty, increment titleCount
    const isTitle = state.src.slice(start, pos).trim() === markerFence && state.src.slice(pos, max).trim() !== '';
    if (isTitle) titleCount++;

    // Unclosed block should be autoclosed by end of document.
    if (nextLine >= endLine) break;
    start = state.bMarks[nextLine] + state.tShift[nextLine];
    max = state.eMarks[nextLine];

    // Non-empty line with negative indent should stop the list:
    // - ```
    //  test
    if (start < max && state.sCount[nextLine] < state.blkIndent) break;
    if (state.src.charCodeAt(start) !== PLUS_MARKER) {
      if (isEmpty) isEmpty = isWhitespace(state, start, max);
      continue;
    }

    // Closing marker should be indented less than 4 spaces
    if (state.sCount[nextLine] - state.blkIndent >= 4) continue;
    pos = state.skipChars(start, PLUS_MARKER);
    // Closing marker must be at least as long as the opening one
    if (pos - start < markerCount) continue;

    // Make sure tail has spaces only
    pos = state.skipSpaces(pos);
    if (pos < max) continue;
    autoClosedBlock = true;

    // Encountered a closing marker line +++
    // If more than 1 title line has been counted, decrement and continue parsing through lines
    if (titleCount > 1) {
      titleCount--;
      continue;
    }
    break;
  }
  if (isEmpty) return false;
  const oldParent = state.parentType;
  const oldLineMax = state.lineMax;
  state.parentType = 'reference';

  // This will prevent lazy continuations from ever going past our end marker
  state.lineMax = nextLine;
  const EXPAND_COLLAPSE_VARIANTS = {
    DEFAULT: 'default',
    CONTAINER: 'container'
  };

  // define default parameter values
  let headerText = params;
  let isOpen = false;
  let variant = EXPAND_COLLAPSE_VARIANTS.DEFAULT;
  let headingLevel = 'h2';

  // check for expand collapse parameters
  const PARAMS_EXP = new RegExp('{{(.*?)}}', 'i');
  const optionalParams = PARAMS_EXP.exec(params);
  if (optionalParams) {
    // checks first line for parameters and splits into passedParams and headerText
    const baseFirstLineString = optionalParams[0];
    const passedParams = optionalParams[1].split(',').map(elem => elem.trim());
    headerText = headerText.replace(baseFirstLineString, '').trim();

    // parse through params and update values
    passedParams.forEach(param => {
      if (param.includes('headingLevel')) {
        const headingLevelStr = param.split('=');
        const level = parseInt(headingLevelStr[1]);
        if (level > 0 && level <= 5) {
          headingLevel = "h".concat(level);
        }
      } else {
        switch (param) {
          case EXPAND_COLLAPSE_VARIANTS.CONTAINER:
            variant = EXPAND_COLLAPSE_VARIANTS.CONTAINER;
            break;
          case 'open':
            isOpen = true;
            break;
        }
      }
    });
  }
  let token = state.push('collapsible', 'div', 0);
  token.block = true;
  token.meta = {
    isOpen,
    variant,
    headerText,
    headingLevel
  };

  // Grab markdown between open and closing markers +++
  const startingPoint = state.eMarks[startLine];
  const endingPoint = state.eMarks[nextLine - 1] + state.sCount[nextLine];
  const markup = state.src.slice(startingPoint, endingPoint).trim();
  token.map = [startLine, nextLine];
  token.content = params;
  token.children = state.md.parse(markup, state.env);
  state.parentType = oldParent;
  state.lineMax = oldLineMax;
  state.line = nextLine + (autoClosedBlock ? 1 : 0);
  return true;
};
const collapsiblePlugin = md => {
  md.block.ruler.before('fence', 'collapsible', coreRule, {
    alt: ['paragraph', 'reference', 'blockquote', 'list']
  });
  md.renderer.rules.collapsible = renderCollapsible;
};
var ExpandCollapse = (() => ({
  plugin: collapsiblePlugin
}));

export { ExpandCollapse as default };
